import React, { useState, useEffect, useRef, useCallback } from 'react';
import faqData from './data/faq.json';

// ── Respect prefers-reduced-motion ──
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ── Hook : révèle un élément quand il entre dans le viewport ──
function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (prefersReducedMotion()) { setVisible(true); return; }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.15, ...options });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

// ── Compteur animé ──
function AnimatedCounter({ target, suffix = '', duration = 1200 }) {
  const [count, setCount] = useState(0);
  const [ref, visible] = useScrollReveal();
  useEffect(() => {
    if (!visible) return;
    if (prefersReducedMotion()) { setCount(target); return; }
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [visible, target, duration]);
  return <span ref={ref}>{count}{suffix}</span>;
}

// Palette "navy premium" (design system SaaS pro/confiance)
const NAVY = '#0f172a'; // navy quasi-noir (fonds sombres, logo)
const B = '#0369a1';    // bleu CTA — plus sophistiqué que le bleu générique
const BD = '#075985';   // bleu CTA foncé hover
const BG = '#f8fafc';   // fond global slate très clair
const TX = '#020617';   // texte principal (navy quasi-noir)
const SB = '#475569';   // texte secondaire slate
const WH = '#ffffff';
const F_HEAD = "'Poppins', sans-serif"; // titres
const F_BODY = "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"; // texte

// --- SVG Icons ---
const IconCalendar = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IconFile = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);
const IconUsers = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IconZap = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);
const IconStar = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const IconCard = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);
const IconSparkles = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8"/>
  </svg>
);
const IconMapPin = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconChat = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
  </svg>
);
const IconGauge = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={B} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/><path d="M12 3a9 9 0 0 0-9 9c0 2.4.9 4.5 2.4 6.2M21 12a9 9 0 0 0-3.4-7"/><path d="M12 15l4-4"/>
  </svg>
);
const IconCheck = ({ color = '#2563eb', size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconChevron = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

function Logo({ size = 26 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ width: size, height: size, borderRadius: '7px', background: `linear-gradient(135deg, ${B}, ${BD})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${B}55` }}>
        <svg width={size * 0.58} height={size * 0.58} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </div>
      <span style={{ fontSize: size * 0.72, fontWeight: 700, color: TX, letterSpacing: '-0.02em', fontFamily: F_HEAD }}>
        Planif<span style={{ color: B }}>Pro</span>
      </span>
    </div>
  );
}

// ── Pill interactive ──
function Pill({ icon, text, anchor }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={() => document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' })}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '9px 18px',
        background: hov ? '#eff6ff' : '#f8faff',
        border: `1.5px solid ${hov ? '#2563eb' : '#e8ecf8'}`,
        borderRadius: '100px', cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(.4,0,.2,1)',
        transform: hov ? 'translateY(-2px)' : 'none',
        boxShadow: hov ? '0 4px 14px rgba(37,99,235,0.2)' : 'none',
      }}
    >
      {icon}
      <span style={{ fontSize: '13px', fontWeight: 600, color: hov ? '#2563eb' : '#374151', transition: 'color 0.2s' }}>{text}</span>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={hov ? '#2563eb' : '#9ca3af'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.2s', transform: hov ? 'translateX(2px)' : 'none' }}><polyline points="6 9 12 15 18 9"/></svg>
    </button>
  );
}

// ── Wrapper reveal au scroll ──
function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(28px)',
      transition: `opacity 0.6s cubic-bezier(.4,0,.2,1) ${delay}ms, transform 0.6s cubic-bezier(.4,0,.2,1) ${delay}ms`,
      ...style,
    }}>{children}</div>
  );
}

function FAQItem({ q, r }) {
  const [open, setOpen] = useState(false);
  return (
    <div onClick={() => setOpen(!open)} style={{ borderBottom: '1px solid #ececec', cursor: 'pointer', userSelect: 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0', color: open ? B : TX }}>
        <span style={{ fontWeight: 600, fontSize: '16px', paddingRight: '16px' }}>{q}</span>
        <IconChevron open={open} />
      </div>
      {open && <p style={{ fontSize: '15px', color: SB, lineHeight: '1.7', paddingBottom: '20px', marginTop: '-4px' }}>{r}</p>}
    </div>
  );
}

// Bouton primaire Apple-style
function BtnPrimary({ children, onClick, large }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: large ? '16px 40px' : '13px 28px',
        background: hov ? BD : B,
        color: WH,
        border: 'none',
        borderRadius: '980px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: large ? '18px' : '15px',
        letterSpacing: '-0.01em',
        boxShadow: hov ? `0 8px 30px ${B}55` : `0 4px 16px ${B}40`,
        transform: hov ? 'translateY(-2px)' : 'none',
        transition: 'all 0.22s cubic-bezier(.4,0,.2,1)',
        whiteSpace: 'nowrap',
      }}
    >{children}</button>
  );
}

function BtnGhost({ children, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '13px 28px',
        background: hov ? '#f0f4ff' : 'transparent',
        color: B,
        border: `1.5px solid ${hov ? B : '#c7d7fd'}`,
        borderRadius: '980px',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: '15px',
        letterSpacing: '-0.01em',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
      }}
    >{children}</button>
  );
}

const METIERS_TW = ["les artisans.", "les DJs.", "la beauté.", "l'aide à domicile.", "tous les indépendants."];

export default function LandingPage({ onLogin, onRegister, onLegal, onBlog, showCookieBanner, onAcceptCookies }) {
  const [scrolled, setScrolled] = useState(false);
  const [twIndex, setTwIndex] = useState(0);
  const [twText, setTwText] = useState('');
  const [twDeleting, setTwDeleting] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Typewriter effect
  useEffect(() => {
    if (prefersReducedMotion()) { setTwText(METIERS_TW[0]); return; }
    const full = METIERS_TW[twIndex];
    let timeout;
    if (!twDeleting && twText.length < full.length) {
      timeout = setTimeout(() => setTwText(full.slice(0, twText.length + 1)), 80);
    } else if (!twDeleting && twText.length === full.length) {
      timeout = setTimeout(() => setTwDeleting(true), 1800);
    } else if (twDeleting && twText.length > 0) {
      timeout = setTimeout(() => setTwText(twText.slice(0, -1)), 45);
    } else if (twDeleting && twText.length === 0) {
      setTwDeleting(false);
      setTwIndex(i => (i + 1) % METIERS_TW.length);
    }
    return () => clearTimeout(timeout);
  }, [twText, twDeleting, twIndex]);

  const features = [
    { icon: <IconCalendar />, titre: 'Planning intelligent', desc: 'Planifiez interventions, bookings et rendez-vous en quelques clics. Vue calendrier claire, pensée pour mobile et desktop.' },
    { icon: <IconFile />, titre: 'Rapports & factures PDF', desc: 'Photos avant/pendant/après, signatures tactiles, SIRET et TVA. Un PDF professionnel généré en un clic.' },
    { icon: <IconUsers />, titre: 'Portail client', desc: 'Vos clients suivent leurs interventions, consultent rapports, photos et factures depuis leur espace — sans vous appeler.' },
    { icon: <IconFile />, titre: 'Devis en 1 clic', desc: 'Créez un devis ligne par ligne, envoyez-le, et convertissez-le en facture dès qu\'il est accepté. Zéro ressaisie.' },
    { icon: <IconZap />, titre: 'Facturation électronique 2026', desc: 'Export Factur-X conforme à la réforme. Vos factures B2B prêtes pour l\'obligation légale qui arrive. Anticipez sereinement.' },
    { icon: <IconCalendar />, titre: 'Rappels automatiques', desc: 'Un email de rappel envoyé automatiquement à vos clients la veille du rendez-vous. Fini les oublis et les lapins.' },
    { icon: <IconZap />, titre: 'Tour de contrôle', desc: 'Un écran qui affiche l\'essentiel chaque matin : retards, factures à envoyer, impayés, tournée du jour optimisée automatiquement. Vous savez où agir.' },
    { icon: <IconStar />, titre: 'Fidélisation clients', desc: 'Carte de fidélité à points visible par vos clients, mémoire des préférences de chacun, et relances automatiques pour ceux à recontacter.' },
    { icon: <IconCard />, titre: 'Statistiques & suivi', desc: 'CA par mois, top clients, taux de recouvrement. Suivez les paiements et pilotez votre activité d\'un coup d\'œil.' },
    { icon: <IconChat />, titre: 'Messagerie & suivi en direct', desc: 'Échangez en temps réel avec vos clients, et partagez votre position en un tap pendant le trajet vers une intervention.' },
    { icon: <IconMapPin />, titre: 'Annuaire de proximité', desc: 'Soyez trouvé par de nouveaux clients près de chez vous, avec votre note moyenne et vos avis affichés.' },
    { icon: <IconGauge />, titre: 'Score de santé entreprise', desc: 'Un score sur 100 qui résume facturation, recouvrement, ponctualité et satisfaction client — en un coup d\'œil.' },
  ];

  // ── Cadre navigateur réutilisable pour les visuels produit ──
  const BrowserFrame = ({ title, children, accent = B }) => (
    <div style={{ borderRadius: '20px', overflow: 'hidden', background: WH, border: '1px solid #e8ecf8', boxShadow: '0 30px 70px rgba(37,99,235,0.12)' }}>
      <div style={{ height: '40px', background: '#f7f9ff', borderBottom: '1px solid #eef2ff', display: 'flex', alignItems: 'center', padding: '0 14px', gap: '7px' }}>
        {['#ff5f57', '#febc2e', '#28c840'].map((c, i) => <span key={i} style={{ width: '10px', height: '10px', borderRadius: '50%', background: c }} />)}
        <span style={{ marginLeft: '10px', fontSize: '12px', color: '#9aa3b2', fontWeight: 600 }}>{title}</span>
        <span style={{ marginLeft: 'auto', width: '7px', height: '7px', borderRadius: '50%', background: accent }} />
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  );

  const showcase = [
    {
      tag: 'Planning',
      icon: <IconCalendar />,
      titre: 'Votre semaine, claire en un coup d\'œil',
      desc: 'Interventions, bookings et rendez-vous sur un calendrier visuel. Ajoutez un créneau en 30 secondes, filtrez par technicien, évitez les doubles réservations.',
      points: ['Vue mois & jour, pensée mobile', 'Filtre par technicien / agent', 'Itinéraire GPS en un tap'],
      visual: (
        <BrowserFrame title="PlanifPro — Planning">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontWeight: 700, fontSize: '15px', color: TX }}>Juin 2026</span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <span style={{ padding: '3px 10px', background: '#f1f5ff', borderRadius: '980px', fontSize: '12px', color: SB }}>‹</span>
              <span style={{ padding: '3px 10px', background: B, borderRadius: '980px', fontSize: '12px', color: WH }}>›</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px' }}>
            {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: '10px', color: SB, fontWeight: 600, paddingBottom: '4px' }}>{d}</div>)}
            {Array.from({ length: 28 }).map((_, i) => {
              const ev = { 3: B, 9: '#10b981', 10: B, 15: '#f59e0b', 16: B, 17: '#10b981', 22: B };
              return (
                <div key={i} style={{ aspectRatio: '1', borderRadius: '7px', background: ev[i] || '#f4f6fc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '10px', fontWeight: ev[i] ? 700 : 500, color: ev[i] ? WH : '#9aa3b2' }}>{i + 1}</span>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '14px', marginTop: '14px' }}>
            {[['Planifié', B], ['En cours', '#f59e0b'], ['Terminé', '#10b981']].map(([l, c], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '3px', background: c }} />
                <span style={{ fontSize: '11px', color: SB }}>{l}</span>
              </div>
            ))}
          </div>
        </BrowserFrame>
      ),
    },
    {
      tag: 'Devis → Facture',
      icon: <IconFile />,
      titre: 'Du devis à la facture, sans ressaisie',
      desc: 'Créez un devis ligne par ligne, envoyez-le, et convertissez-le en facture d\'un seul clic dès qu\'il est accepté. La TVA se calcule toute seule.',
      points: ['Catalogue de prestations réutilisable', 'Conversion en facture en 1 clic', 'Export PDF & Factur-X 2026'],
      visual: (
        <BrowserFrame title="PlanifPro — Devis" accent="#10b981">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <p style={{ fontSize: '11px', color: SB, margin: 0 }}>Devis DEV-2026-0042</p>
              <p style={{ fontSize: '15px', fontWeight: 700, color: TX, margin: '2px 0 0' }}>Rénovation salle de bain</p>
            </div>
            <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '980px', background: '#dcfce7', color: '#16a34a' }}>Accepté</span>
          </div>
          {[['Main d\'œuvre (8h)', '320,00'], ['Robinetterie', '145,00'], ['Carrelage 6 m²', '210,00']].map(([l, p], i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontSize: '13px', color: '#374151' }}>{l}</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: TX }}>{p} €</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: TX }}>Total HT&nbsp;: 675,00 €</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: B, color: WH, fontSize: '12px', fontWeight: 700, padding: '8px 14px', borderRadius: '980px' }}>🧾 → Facture</span>
          </div>
        </BrowserFrame>
      ),
    },
    {
      tag: 'Portail client',
      icon: <IconUsers />,
      titre: 'Vos clients suivent tout, sans vous appeler',
      desc: 'Chaque client accède à son espace : interventions à venir, comptes-rendus, photos avant/après et factures à télécharger. Vous gagnez en image et en tranquillité.',
      points: ['Rapports & photos consultables', 'Factures téléchargeables en PDF', 'Demandes de rendez-vous en ligne'],
      visual: (
        <BrowserFrame title="Espace client" accent="#7c3aed">
          <div style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563eb)', borderRadius: '14px', padding: '16px 18px', color: WH, marginBottom: '14px' }}>
            <p style={{ fontSize: '11px', opacity: 0.85, margin: 0 }}>Prochaine intervention</p>
            <p style={{ fontSize: '16px', fontWeight: 700, margin: '4px 0 0' }}>Jeudi 25 juin · 09:00</p>
            <p style={{ fontSize: '12px', opacity: 0.85, margin: '2px 0 0' }}>Entretien chaudière — Cédric M.</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {['#f59e0b', B, '#10b981'].map((c, i) => (
              <div key={i} style={{ flex: 1, aspectRatio: '1.3', borderRadius: '8px', background: c + '18', border: `1.5px dashed ${c}55`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#eff6ff', borderRadius: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e3a8a' }}>Facture FAC-2026-018 · 130 €</span>
            <span style={{ fontSize: '12px', fontWeight: 700, color: B }}>⬇️ PDF</span>
          </div>
        </BrowserFrame>
      ),
    },
  ];

  // Pas de témoignages clients tant qu'il n'y en a pas de vrais : des avis
  // inventés sont une pratique commerciale trompeuse (art. L121-2 du Code de
  // la consommation). À remplacer par de vrais avis dès qu'il y en a — la
  // table `avis` et la page /avis/:id existent déjà pour les collecter.
  const garanties = [
    { emoji: '🎁', titre: '10 jours pour essayer', texte: "Toutes les fonctionnalités débloquées, aucune carte bancaire demandée. Si ça ne vous va pas, vous ne faites rien et le compte s'arrête tout seul." },
    { emoji: '🇫🇷', titre: 'Données hébergées en France', texte: "Vos clients, vos factures et vos chiffres restent sur des serveurs européens. Pas de revente de données, pas de publicité." },
    { emoji: '📄', titre: 'Vos données sont à vous', texte: "Factures en PDF, export Factur-X pour votre comptable. Si vous partez un jour, vous partez avec tout." },
    { emoji: '🛠️', titre: 'Un outil, pas une usine à gaz', texte: "Pensé pour une personne seule ou une petite équipe. Pas de module à configurer pendant trois jours avant de pouvoir facturer." },
  ];

  const planFeatures = [
    'Planning & interventions illimitées',
    'Rapports PDF pro (photos + signatures)',
    'Devis & conversion en facture',
    'Facturation électronique (Factur-X)',
    'Portail client (suivi en ligne)',
    'Rappels RDV automatiques par email',
    'Tour de contrôle & statistiques CA',
    'Bouton itinéraire GPS',
    'Mode sombre & accès mobile',
  ];

  const faq = faqData; // source unique partagée avec le pré-rendu SEO (FAQPage JSON-LD)

  const s = { // shared shorthand styles
    section: { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' },
    label: { fontSize: '13px', fontWeight: 700, color: B, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px', display: 'block' },
    h2: { fontSize: 'clamp(30px, 4.5vw, 52px)', fontWeight: 700, letterSpacing: '-0.025em', color: TX, lineHeight: 1.1, fontFamily: F_HEAD },
  };

  return (
    <div style={{ minHeight: '100vh', background: WH, color: TX, fontFamily: F_BODY, overflowX: 'hidden' }}>
      <style>{`
        @keyframes blobFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-24px) scale(1.04); }
        }
        @keyframes blobFloat2 {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(18px) scale(0.97); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; } 50% { opacity: 0; }
        }
        .reveal-enter {
          animation: fadeSlideUp 0.6s cubic-bezier(.4,0,.2,1) forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .reveal-enter { animation: none; opacity: 1; }
          [data-blob] { animation: none !important; }
        }
        /* Mockup : stats en 2 colonnes sur mobile */
        @media (max-width: 600px) {
          .pm-hero-stats { grid-template-columns: repeat(2, 1fr) !important; }
          .pm-nav { padding: 0 16px !important; }
        }
        /* Bande de stats : 2 colonnes sous 760px */
        @media (max-width: 760px) {
          .pm-stats-band { grid-template-columns: repeat(2, 1fr) !important; gap: 32px 16px !important; }
        }
        /* Mockup hero : sidebar plus fine sur petit écran */
        @media (max-width: 480px) {
          .pm-hero-side { width: 48px !important; }
          .pm-hero-app { min-height: 0 !important; }
        }
        /* Vitrine alternée : empile en 1 colonne (visuel au-dessus du texte) sur mobile */
        @media (max-width: 860px) {
          .pm-showcase-row { grid-template-columns: 1fr !important; }
          .pm-showcase-row > div { order: 0 !important; }
          .pm-showcase-row > div:first-child { order: 2 !important; }
          .pm-showcase-row > div:last-child { order: 1 !important; }
        }
      `}</style>

      {/* ── COOKIE BANNER ── */}
      {showCookieBanner && (
        <div style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: 'calc(100% - 40px)', maxWidth: '500px', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: '20px', boxShadow: '0 16px 48px rgba(0,0,0,0.14)', border: '1px solid #ececec', padding: '24px', zIndex: 2000 }}>
          <p style={{ fontSize: '14px', fontWeight: 700, marginBottom: '6px' }}>Cookies</p>
          <p style={{ fontSize: '13px', color: SB, lineHeight: '1.5', marginBottom: '16px' }}>
            PlanifPro utilise uniquement des cookies nécessaires à son fonctionnement.{' '}
            <button onClick={() => onLegal('cookies')} style={{ background: 'none', border: 'none', color: B, cursor: 'pointer', fontSize: '13px', padding: 0, textDecoration: 'underline' }}>En savoir plus</button>
          </p>
          <BtnPrimary onClick={onAcceptCookies}>J'ai compris</BtnPrimary>
        </div>
      )}

      {/* ── NAVBAR ── */}
      <header className="pm-nav" style={{
        position: 'sticky', top: 0, zIndex: 200,
        height: '60px', padding: '0 32px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: scrolled ? 'rgba(248,250,252,0.72)' : 'rgba(255,255,255,0)',
        backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(15,23,42,0.06)' : '1px solid transparent',
        boxShadow: scrolled ? '0 4px 24px rgba(15,23,42,0.05)' : 'none',
        transition: 'all 0.3s',
      }}>
        <Logo size={26} />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={onLogin} style={{ padding: '8px 18px', background: 'transparent', color: TX, border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 500, fontSize: '14px', transition: 'color 0.15s' }}
            onMouseEnter={e => e.target.style.color = B}
            onMouseLeave={e => e.target.style.color = TX}
          >Se connecter</button>
          <BtnPrimary onClick={onRegister}>Commencer</BtnPrimary>
        </div>
      </header>

      {/* ── HERO ── */}
      <section style={{ position: 'relative', padding: 'clamp(80px, 11vw, 140px) 24px clamp(60px, 8vw, 100px)', textAlign: 'center', overflow: 'hidden' }}>
        {/* glow orbs animés */}
        <div style={{ position: 'absolute', top: '-120px', left: '50%', transform: 'translateX(-50%)', width: '900px', height: '600px', background: `radial-gradient(ellipse at 50% 0%, ${B}18 0%, transparent 65%)`, pointerEvents: 'none' }} />
        <div data-blob style={{ position: 'absolute', top: '30%', left: '-10%', width: '400px', height: '400px', borderRadius: '50%', background: `radial-gradient(circle, ${B}0e 0%, transparent 70%)`, pointerEvents: 'none', animation: 'blobFloat 7s ease-in-out infinite' }} />
        <div data-blob style={{ position: 'absolute', top: '20%', right: '-8%', width: '360px', height: '360px', borderRadius: '50%', background: `radial-gradient(circle, #1e40af0e 0%, transparent 70%)`, pointerEvents: 'none', animation: 'blobFloat2 9s ease-in-out infinite' }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '860px', margin: '0 auto' }}>
          {/* badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '100px', padding: '5px 14px 5px 8px', marginBottom: '36px' }}>
            <span style={{ background: B, color: WH, fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '100px' }}>NOUVEAU</span>
            <span style={{ fontSize: '13px', fontWeight: 500, color: B }}>Prix de lancement — offre limitée</span>
          </div>

          <h1 style={{ fontSize: 'clamp(32px, 7vw, 82px)', fontWeight: 700, lineHeight: 1.06, letterSpacing: '-0.035em', marginBottom: '28px', color: TX, wordBreak: 'break-word', fontFamily: F_HEAD }}>
            L'outil pensé pour<br />
            <span style={{ color: B }}>
              {twText}
              <span style={{ display: 'inline-block', width: '3px', height: '0.85em', background: B, marginLeft: '3px', verticalAlign: 'text-bottom', animation: 'cursorBlink 1s step-end infinite' }} />
            </span>
          </h1>

          <p style={{ fontSize: 'clamp(18px, 2.2vw, 23px)', color: SB, lineHeight: 1.55, maxWidth: '620px', margin: '0 auto 44px', fontWeight: 400, letterSpacing: '-0.01em' }}>
            Planning, rapports et factures PDF, portail client, fidélité et relances. L'outil tout-en-un pour tout auto-entrepreneur ou petite entreprise de service, quel que soit son métier.
          </p>

          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '14px' }}>
            <BtnPrimary large onClick={onRegister}>Essayer gratuitement</BtnPrimary>
            <BtnGhost onClick={() => document.getElementById('tarifs')?.scrollIntoView({ behavior: 'smooth' })}>Voir les tarifs →</BtnGhost>
          </div>

          {/* Lever l'objection au moment exact du clic : c'est là que se perd
              un visiteur qui craint de devoir sortir sa carte pour voir. */}
          <p style={{ fontSize: '14px', color: SB, marginBottom: '72px', fontWeight: 500 }}>
            10 jours gratuits · aucune carte bancaire demandée · sans engagement
          </p>

          {/* ── APP MOCKUP ── */}
          <div style={{
            background: 'linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)',
            borderRadius: '28px',
            padding: '3px',
            boxShadow: `0 60px 120px ${B}20, 0 20px 40px rgba(0,0,0,0.06)`,
          }}>
            <div style={{ background: WH, borderRadius: '26px', overflow: 'hidden' }}>
              {/* window chrome */}
              <div style={{ height: '44px', background: '#f7f9ff', borderBottom: '1px solid #eef2ff', display: 'flex', alignItems: 'center', padding: '0 18px', gap: '7px' }}>
                {['#ff5f57','#febc2e','#28c840'].map((c, i) => <div key={i} style={{ width: '11px', height: '11px', borderRadius: '50%', background: c }} />)}
                <span style={{ marginLeft: '12px', fontSize: '13px', color: '#9aa3b2', fontWeight: 600 }}>app.planifpro.fr</span>
              </div>

              {/* corps : sidebar + contenu */}
              <div className="pm-hero-app" style={{ display: 'flex', minHeight: '340px', textAlign: 'left' }}>
                {/* mini sidebar */}
                <div className="pm-hero-side" style={{ width: '64px', background: NAVY, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '18px 0', gap: '8px' }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: `linear-gradient(135deg, ${B}, ${BD})`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  {[true, false, false, false, false].map((active, i) => (
                    <div key={i} style={{ width: '36px', height: '36px', borderRadius: '10px', background: active ? 'rgba(37,99,235,0.25)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: '16px', height: '16px', borderRadius: '5px', background: active ? '#60a5fa' : '#2b3350' }} />
                    </div>
                  ))}
                </div>

                {/* contenu */}
                <div style={{ flex: 1, padding: '20px 22px', background: BG, minWidth: 0 }}>
                  <p style={{ fontSize: '12px', color: SB, margin: 0 }}>Bonjour Cédric 👋</p>
                  <p style={{ fontSize: '17px', fontWeight: 800, color: TX, margin: '2px 0 16px', letterSpacing: '-0.02em' }}>Votre journée</p>

                  {/* KPIs */}
                  <div className="pm-hero-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '18px' }}>
                    {[['6', 'RDV aujourd\'hui', B], ['1 240€', 'Encaissé', '#10b981'], ['3', 'À facturer', '#f59e0b']].map(([v, l, c], i) => (
                      <div key={i} style={{ background: WH, borderRadius: '12px', padding: '12px 14px', border: '1px solid #eef2ff' }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: c }}>{v}</div>
                        <div style={{ fontSize: '10px', color: SB, marginTop: '2px', fontWeight: 500 }}>{l}</div>
                      </div>
                    ))}
                  </div>

                  {/* agenda du jour */}
                  <p style={{ fontSize: '11px', fontWeight: 700, color: SB, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px' }}>Prochaines interventions</p>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {[
                      ['09:00', 'Fuite sous-évier', 'M. Dupont', '#10b981', 'Terminé'],
                      ['11:30', 'Installation radiateur', 'Mme Bernard', '#f59e0b', 'En cours'],
                      ['14:00', 'Diagnostic chaudière', 'M. Moreau', B, 'À venir'],
                    ].map(([h, t, c, col, st], i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: WH, borderRadius: '12px', padding: '11px 14px', border: '1px solid #eef2ff' }}>
                        <span style={{ width: '4px', alignSelf: 'stretch', borderRadius: '4px', background: col, flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', fontWeight: 700, color: TX, width: '38px', flexShrink: 0 }}>{h}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: '13px', fontWeight: 600, color: TX, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t}</p>
                          <p style={{ fontSize: '11px', color: SB, margin: '1px 0 0' }}>{c}</p>
                        </div>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 9px', borderRadius: '980px', background: col + '1a', color: col, flexShrink: 0 }}>{st}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BANDEAU DE RÉASSURANCE ── */}
      <div style={{ borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', padding: '22px 24px', background: WH }}>
        <div style={{ maxWidth: '920px', margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'clamp(16px, 4vw, 40px)', flexWrap: 'wrap' }}>
          {/* "Satisfait ou remboursé" retiré : aucune politique de
              remboursement n'est écrite nulle part, et c'est un engagement
              qu'on ne peut pas tenir à moitié. "Sans carte bancaire" est
              vrai, vérifiable, et lève la même objection à l'inscription. */}
          {['10 jours d\'essai gratuit', 'Sans carte bancaire', 'Sans engagement', 'Conforme Factur-X 2026'].map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ flexShrink: 0, width: '20px', height: '20px', borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconCheck color="#16a34a" size={12} />
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{t}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── BANDE STATS ── */}
      <section style={{ padding: 'clamp(56px, 7vw, 84px) 0', background: WH }}>
        <div style={s.section}>
          <div className="pm-stats-band" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
            {[
              { v: 4, suf: '', label: 'métiers couverts', sub: 'Artisan · DJ · Beauté · Aide à domicile' },
              { v: 5, suf: ' min', label: 'pour un devis', sub: 'De la saisie au PDF envoyé' },
              { v: 100, suf: '%', label: 'conforme Factur-X', sub: 'Prêt pour la facturation 2026' },
              { v: 10, suf: ' j', label: 'd\'essai gratuit', sub: 'Sans engagement, sans carte' },
            ].map((st, i) => (
              <Reveal key={i} delay={i * 90} style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 'clamp(34px, 4.5vw, 52px)', fontWeight: 800, letterSpacing: '-0.03em', color: B, lineHeight: 1 }}>
                  <AnimatedCounter target={st.v} suffix={st.suf} />
                </p>
                <p style={{ fontSize: '15px', fontWeight: 700, color: TX, marginTop: '10px' }}>{st.label}</p>
                <p style={{ fontSize: '13px', color: SB, marginTop: '4px', lineHeight: 1.5 }}>{st.sub}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES — vitrine alternée (style Appcues) ── */}
      <section id="features" style={{ padding: 'clamp(80px, 10vw, 120px) 0' }}>
        <div style={s.section}>
          <Reveal style={{ textAlign: 'center', marginBottom: 'clamp(48px, 7vw, 88px)' }}>
            <span style={s.label}>Fonctionnalités</span>
            <h2 style={s.h2}>Tout votre métier, au même endroit.</h2>
            <p style={{ fontSize: '19px', color: SB, maxWidth: '560px', margin: '16px auto 0', lineHeight: 1.5 }}>Du premier rendez-vous au dernier euro encaissé : un outil qui s'adapte à votre activité, pas l'inverse.</p>
          </Reveal>

          {showcase.map((row, i) => {
            const reversed = i % 2 === 1;
            return (
              <Reveal key={i} delay={60}>
                <div className="pm-showcase-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(32px, 6vw, 80px)', alignItems: 'center', marginBottom: 'clamp(64px, 9vw, 120px)' }}>
                  {/* Texte */}
                  <div style={{ order: reversed ? 2 : 1 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: '100px', marginBottom: '20px' }}>
                      <span style={{ display: 'flex' }}>{row.icon}</span>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: B }}>{row.tag}</span>
                    </div>
                    <h3 style={{ fontSize: 'clamp(26px, 3.4vw, 38px)', fontWeight: 700, letterSpacing: '-0.025em', color: TX, lineHeight: 1.12, marginBottom: '16px', fontFamily: F_HEAD }}>{row.titre}</h3>
                    <p style={{ fontSize: '17px', color: SB, lineHeight: 1.65, marginBottom: '24px' }}>{row.desc}</p>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {row.points.map((p, j) => (
                        <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ flexShrink: 0, width: '22px', height: '22px', borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconCheck color="#16a34a" size={13} /></span>
                          <span style={{ fontSize: '15px', color: '#374151', fontWeight: 500 }}>{p}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Visuel produit en cadre */}
                  <div style={{ order: reversed ? 1 : 2 }}>{row.visual}</div>
                </div>
              </Reveal>
            );
          })}

          {/* Grille du reste des fonctionnalités */}
          <Reveal style={{ textAlign: 'center', margin: 'clamp(24px, 4vw, 48px) 0 40px' }}>
            <h3 style={{ fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: 700, letterSpacing: '-0.02em', color: TX, fontFamily: F_HEAD }}>Et tout ce qu'il faut autour.</h3>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {features.slice(3).map((f, i) => (
              <Reveal key={i} delay={i * 70}>
                <FeatureCard icon={f.icon} titre={f.titre} desc={f.desc} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── DARK BAND — PDF ── */}
      <section style={{ background: NAVY, color: WH, padding: 'clamp(80px, 10vw, 130px) 24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 30% 50%, ${B}14 0%, transparent 60%)`, pointerEvents: 'none' }} />
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '72px', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <div>
            <span style={{ ...s.label, color: '#60a5fa' }}>Rapports & comptes-rendus PDF</span>
            <h2 style={{ ...s.h2, color: WH, marginBottom: '20px' }}>Un document qui inspire confiance.</h2>
            <p style={{ fontSize: '18px', color: '#a1a1a6', lineHeight: 1.65, marginBottom: '28px' }}>Vos clients reçoivent un compte-rendu professionnel à la fin de chaque intervention — généré automatiquement, sans logiciel de bureautique ni mise en page à refaire.</p>
            <div style={{ display: 'grid', gap: '14px', marginBottom: '36px' }}>
              {[
                'Photos avant / pendant / après horodatées',
                'Signatures du client et du technicien sur l\'écran',
                'Votre logo, SIRET et TVA pré-remplis',
                'Export PDF & Factur-X — prêt pour 2026',
              ].map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ flexShrink: 0, width: '24px', height: '24px', borderRadius: '50%', background: 'rgba(96,165,250,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconCheck color="#60a5fa" size={14} />
                  </span>
                  <span style={{ fontSize: '15px', color: '#e5e7eb', fontWeight: 500 }}>{t}</span>
                </div>
              ))}
            </div>
            <BtnPrimary onClick={onRegister}>Commencer gratuitement →</BtnPrimary>
          </div>

          {/* PDF card */}
          <div style={{ position: 'relative', maxWidth: '400px', margin: '0 auto', width: '100%' }}>
            {/* carte fantôme en profondeur */}
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.06)', borderRadius: '20px', transform: 'rotate(-4deg) translateY(10px)' }} />
            {/* badge flottant */}
            <div style={{ position: 'absolute', top: '-16px', right: '-10px', zIndex: 3, background: '#10b981', color: WH, fontSize: '12px', fontWeight: 700, padding: '8px 14px', borderRadius: '980px', boxShadow: '0 10px 30px rgba(16,185,129,0.4)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <IconCheck color="#fff" size={13} /> Généré en 1 clic
            </div>

            <div style={{ position: 'relative', zIndex: 2, background: WH, borderRadius: '20px', overflow: 'hidden', boxShadow: `0 40px 90px ${B}45`, color: TX }}>
              <div style={{ background: `linear-gradient(135deg, #1e3a8a, ${B})`, padding: '24px 26px', color: WH }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: '10px', opacity: 0.8, marginBottom: '4px', letterSpacing: '0.12em' }}>BON D'INTERVENTION</p>
                    <p style={{ fontSize: '20px', fontWeight: 800 }}>N° 2026-0616</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '15px', fontWeight: 800 }}>Plomberie KB</p>
                    <p style={{ fontSize: '10px', opacity: 0.8 }}>SIRET 812 456 …</p>
                  </div>
                </div>
              </div>
              <div style={{ padding: '22px' }}>
                {[['Client', 'M. Dupont'], ['Date', '16/06/2026'], ['Technicien', 'Cédric M.'], ['Nature', 'Débouchage canalisation']].map(([k, v], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontSize: '12px', color: SB }}>{k}</span>
                    <span style={{ fontSize: '12px', fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
                {/* vignettes photos réalistes */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', margin: '16px 0' }}>
                  {[
                    ['Avant', '#f59e0b', 'linear-gradient(135deg,#fde68a,#b45309)'],
                    ['Pendant', B, 'linear-gradient(135deg,#93c5fd,#1e3a8a)'],
                    ['Après', '#10b981', 'linear-gradient(135deg,#86efac,#065f46)'],
                  ].map(([label, c, grad], i) => (
                    <div key={i}>
                      <div style={{ position: 'relative', aspectRatio: '1', borderRadius: '9px', background: grad, overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.12)' }} />
                        <span style={{ position: 'absolute', bottom: '5px', left: '5px', fontSize: '8px', fontWeight: 800, color: WH, background: 'rgba(0,0,0,0.45)', padding: '2px 6px', borderRadius: '5px', letterSpacing: '0.04em' }}>{label.toUpperCase()}</span>
                        <svg style={{ position: 'absolute', top: '6px', right: '6px' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {['Signature client', 'Signature technicien'].map((l, i) => (
                    <div key={i} style={{ flex: 1 }}>
                      <p style={{ fontSize: '9px', color: SB, marginBottom: '4px' }}>{l}</p>
                      <div style={{ height: '40px', borderRadius: '9px', border: `1px solid ${B}25`, background: `${B}08`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                        <svg width="60" height="22" viewBox="0 0 60 22" fill="none" stroke={B} strokeWidth="1.6" strokeLinecap="round"><path d="M3 14 C 8 4, 12 4, 16 12 S 24 20, 28 10 S 36 2, 42 12 S 52 18, 57 8" opacity="0.8"/></svg>
                        <span style={{ position: 'absolute', bottom: '3px', right: '6px', fontSize: '8px', color: B, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '2px' }}><IconCheck size={9} /> Signé</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── POURQUOI / GARANTIES ──
          Remplace l'ancienne section de témoignages inventés. Pour un produit
          jeune, l'histoire vraie du créateur et des engagements vérifiables
          convertissent mieux que des avis cinq étoiles que personne ne croit. */}
      <section style={{ padding: 'clamp(80px, 10vw, 120px) 0', background: BG }}>
        <div style={s.section}>
          <Reveal style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={s.label}>Pourquoi PlanifPro existe</span>
            <h2 style={s.h2}>Construit par un indépendant,<br />pour des indépendants.</h2>
          </Reveal>

          <Reveal>
            <div style={{ background: WH, borderRadius: '24px', padding: 'clamp(28px, 5vw, 48px)', border: '1px solid #e8ecf8', boxShadow: '0 2px 12px rgba(37,99,235,0.05)', maxWidth: '760px', margin: '0 auto 24px' }}>
              <p style={{ fontSize: 'clamp(17px, 2.2vw, 20px)', color: '#1e293b', lineHeight: 1.7, margin: 0 }}>
                PlanifPro n'a pas été pensé dans une réunion produit. Je suis DJ indépendant, et je passais mes dimanches soir à rattraper des devis sur un carnet, des factures sur un tableur et un planning dans ma tête.
              </p>
              <p style={{ fontSize: 'clamp(17px, 2.2vw, 20px)', color: '#1e293b', lineHeight: 1.7, margin: '18px 0 0' }}>
                Les logiciels existants étaient soit hors de prix, soit faits pour des entreprises de cinquante personnes. J'ai construit celui qu'il me fallait — et je m'en sers tous les jours pour ma propre activité.
              </p>
              <p style={{ fontSize: '15px', color: SB, lineHeight: 1.7, margin: '22px 0 0', paddingTop: '20px', borderTop: '1px solid #eef2f7' }}>
                PlanifPro est un projet récent et français. Pas de levée de fonds, pas d'actionnaire à rembourser : juste un outil qui doit être assez utile pour que vous ayez envie de le garder.
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginTop: '36px' }}>
            {garanties.map((g, i) => (
              <Reveal key={i} delay={i * 100}>
                <div style={{ background: WH, borderRadius: '20px', padding: '28px', border: '1px solid #e8ecf8', boxShadow: '0 2px 12px rgba(37,99,235,0.05)', height: '100%' }}>
                  <p style={{ fontSize: '26px', margin: '0 0 12px' }}>{g.emoji}</p>
                  <p style={{ fontSize: '16px', fontWeight: 700, color: TX, margin: '0 0 8px' }}>{g.titre}</p>
                  <p style={{ fontSize: '14px', color: SB, lineHeight: 1.6, margin: 0 }}>{g.texte}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="tarifs" style={{ padding: 'clamp(80px, 10vw, 120px) 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '10%', left: '10%', width: '380px', height: '380px', borderRadius: '50%', background: `radial-gradient(circle, ${B}22 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '5%', right: '8%', width: '340px', height: '340px', borderRadius: '50%', background: 'radial-gradient(circle, #10b98122 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ ...s.section, position: 'relative' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: '56px' }}>
            <span style={s.label}>Tarifs</span>
            <h2 style={s.h2}>Un prix juste.</h2>
            <p style={{ fontSize: '19px', color: SB, marginTop: '14px', maxWidth: '500px', margin: '14px auto 20px' }}>Toutes les fonctionnalités dans chaque formule. Seule la taille d'équipe change.</p>
            <span style={{ display: 'inline-block', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '6px 18px', borderRadius: '100px', fontSize: '13px', fontWeight: 700 }}>Prix de lancement — offre limitée</span>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', alignItems: 'stretch' }}>
            {[
              { name: 'Indépendant', old: '19,99€', price: '14,99€', note: '1 personne', accent: B, popular: false },
              { name: 'Pro', old: '99,99€', price: '74,99€', note: "Jusqu'à 6 personnes", accent: B, popular: true },
              { name: 'Business', old: '159,99€', price: '119,99€', note: "Jusqu'à 12 personnes", accent: '#10b981', popular: false },
            ].map((plan, pi) => (
              <Reveal key={pi} delay={pi * 100}>
                <PricingCard plan={plan} features={planFeatures} onRegister={onRegister} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" style={{ padding: 'clamp(80px, 10vw, 120px) 0', background: BG }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 24px' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span style={s.label}>FAQ</span>
            <h2 style={s.h2}>Questions fréquentes</h2>
          </Reveal>
          {faq.map((item, i) => <FAQItem key={i} q={item.q} r={item.r} />)}
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section style={{ padding: 'clamp(100px, 12vw, 160px) 24px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 50% 50%, ${B}0f 0%, transparent 65%)`, pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '640px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(36px, 6vw, 68px)', fontWeight: 700, letterSpacing: '-0.035em', marginBottom: '22px', lineHeight: 1.05, color: TX, fontFamily: F_HEAD }}>
            Lancez-vous<br />aujourd'hui.
          </h2>
          <p style={{ fontSize: '20px', color: SB, marginBottom: '40px', lineHeight: 1.5 }}>Votre activité mérite un outil à sa hauteur.<br />10 jours gratuits, résiliez avant la fin de l'essai et vous ne serez pas débité.</p>
          <BtnPrimary large onClick={onRegister}>Créer mon compte gratuitement</BtnPrimary>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop: '1px solid #ececec', padding: '48px 24px 32px', background: '#fafbff' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '32px', marginBottom: '40px' }}>
            <div>
              <Logo size={22} />
              <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '12px', lineHeight: 1.6 }}>L'outil de gestion pensé pour les pros de terrain.</p>
            </div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: TX, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Produit</p>
              {[['Fonctionnalités', 'features'], ['Tarifs', 'tarifs'], ['FAQ', 'faq']].map(([label, id]) => (
                <a key={id} href={`#${id}`}
                  onClick={e => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}
                  style={{ display: 'block', fontSize: '14px', color: SB, textDecoration: 'none', marginBottom: '8px', cursor: 'pointer' }}
                  onMouseEnter={e => e.target.style.color = B}
                  onMouseLeave={e => e.target.style.color = SB}
                >{label}</a>
              ))}
            </div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: TX, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ressources</p>
              {onBlog && <button onClick={onBlog} style={{ display: 'block', background: 'none', border: 'none', color: SB, cursor: 'pointer', fontSize: '14px', padding: 0, marginBottom: '8px', textAlign: 'left' }}
                onMouseEnter={e => e.target.style.color = B}
                onMouseLeave={e => e.target.style.color = SB}
              >Blog</button>}
              <a href="/calculateurs" style={{ display: 'block', color: SB, textDecoration: 'none', fontSize: '14px', marginBottom: '8px' }}
                onMouseEnter={e => e.target.style.color = B}
                onMouseLeave={e => e.target.style.color = SB}
              >Calculateurs gratuits</a>
              <button onClick={onRegister} style={{ display: 'block', background: 'none', border: 'none', color: SB, cursor: 'pointer', fontSize: '14px', padding: 0, marginBottom: '8px', textAlign: 'left' }}
                onMouseEnter={e => e.target.style.color = B}
                onMouseLeave={e => e.target.style.color = SB}
              >Essai gratuit</button>
            </div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: TX, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Légal</p>
              {[['mentions', 'Mentions légales'], ['cgv', 'CGV'], ['cgu', 'CGU'], ['cookies', 'Cookies']].map(([tab, label]) => (
                <button key={tab} onClick={() => onLegal(tab)} style={{ display: 'block', background: 'none', border: 'none', color: SB, cursor: 'pointer', fontSize: '14px', padding: 0, marginBottom: '8px', textAlign: 'left' }}
                  onMouseEnter={e => e.target.style.color = B}
                  onMouseLeave={e => e.target.style.color = SB}
                >{label}</button>
              ))}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #ececec', paddingTop: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: '#b0b0b8' }}>&copy; {new Date().getFullYear()} PlanifPro. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, titre, desc }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? WH : BG,
        border: `1px solid ${hov ? '#bfdbfe' : '#e8ecf8'}`,
        borderRadius: '20px',
        padding: '32px',
        transition: 'all 0.22s cubic-bezier(.4,0,.2,1)',
        transform: hov ? 'translateY(-4px)' : 'none',
        boxShadow: hov ? '0 16px 40px rgba(37,99,235,0.1)' : 'none',
        cursor: 'default',
      }}
    >
      <div style={{ width: '50px', height: '50px', borderRadius: '14px', background: hov ? '#eff6ff' : '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', transition: 'background 0.2s' }}>{icon}</div>
      <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '10px', letterSpacing: '-0.015em', color: TX }}>{titre}</h3>
      <p style={{ fontSize: '15px', color: SB, lineHeight: 1.65 }}>{desc}</p>
    </div>
  );
}

function PricingCard({ plan, features, onRegister }) {
  const [hov, setHov] = useState(false);
  const isDark = plan.popular;
  return (
    <div style={{
      background: isDark ? 'rgba(15,23,42,0.78)' : WH,
      backdropFilter: isDark ? 'blur(20px)' : 'none',
      WebkitBackdropFilter: isDark ? 'blur(20px)' : 'none',
      color: isDark ? WH : TX,
      border: isDark ? '1px solid rgba(255,255,255,0.14)' : '1px solid #e8ecf8',
      borderRadius: '24px',
      padding: plan.popular ? '44px 32px' : '38px 32px',
      display: 'flex', flexDirection: 'column',
      position: 'relative',
      boxShadow: isDark ? `0 24px 60px ${plan.accent}25` : '0 2px 12px rgba(37,99,235,0.05)',
      transition: 'transform 0.2s',
      transform: plan.popular ? 'scale(1.02)' : 'none',
    }}>
      {plan.popular && (
        <div style={{ position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)', background: plan.accent, color: WH, padding: '5px 18px', borderRadius: '100px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '0.05em' }}>RECOMMANDÉ</div>
      )}
      <h3 style={{ fontSize: '17px', fontWeight: 600, marginBottom: '20px', color: isDark ? '#94a3b8' : SB }}>{plan.name}</h3>
      <p style={{ fontSize: '15px', color: isDark ? '#4b5563' : '#cbd5e1', textDecoration: 'line-through', marginBottom: '2px' }}>{plan.old}</p>
      <p style={{ fontSize: '50px', fontWeight: 800, letterSpacing: '-0.035em', color: isDark ? WH : TX, lineHeight: 1.1 }}>{plan.price}</p>
      <p style={{ fontSize: '14px', color: isDark ? '#94a3b8' : SB, marginBottom: '6px' }}>par mois</p>
      <p style={{ fontSize: '13px', color: plan.accent, fontWeight: 600, marginBottom: '26px' }}>{plan.note}</p>
      <div style={{ flex: 1, marginBottom: '26px' }}>
        {features.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', marginBottom: '11px' }}>
            <span style={{ flexShrink: 0, marginTop: '1px' }}><IconCheck color={plan.accent} /></span>
            <span style={{ fontSize: '13px', color: isDark ? '#d1d5db' : SB, lineHeight: 1.5 }}>{f}</span>
          </div>
        ))}
      </div>
      <button
        onClick={onRegister}
        onMouseEnter={e => { e.target.style.background = plan.popular ? BD : plan.accent; e.target.style.color = WH; e.target.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={e => { e.target.style.background = plan.popular ? B : 'transparent'; e.target.style.color = plan.popular ? WH : plan.accent; e.target.style.transform = 'none'; }}
        style={{ width: '100%', padding: '13px', background: plan.popular ? B : 'transparent', color: plan.popular ? WH : plan.accent, border: `1.5px solid ${plan.accent}`, borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '15px', transition: 'all 0.2s', letterSpacing: '-0.01em' }}
      >Choisir</button>
    </div>
  );
}
